/* eslint-disable eag/match-named-export */
import {isEmpty, union} from 'ramda';

import {AuditCategoryOfStructure, AuditDataVideoAssetsFilesBody} from '@dms/api/core';

import {getCategoryIdFromFormFieldName} from '../../components/VehicleInspection/utils/getCategoryIdFromFormFieldName';
import {AuditDataAssetsFilesBody} from '../../types/AuditDataAssetsFilesBody';
import {AuditCategoriesWithError, TConditionFormValue} from '../../types/ConditionTypes';
import {LoadAuditDataCategories} from '../../types/LoadAuditDataCategories';
import {LoadAuditDataResponseItemBody} from '../../types/LoadAuditDataResponseItemBody';
import {getFormFieldName} from '../../utils/getFormFieldName';
import {isJsonString} from '../../utils/isJsonString';

export type AuditFields = Record<string, TConditionFormValue>;
export type AuditAsset = Record<string, AuditDataAssetsFilesBody>;
export type AuditAssets = Record<string, AuditAsset>;
export type AuditVideoAsset = Record<string, AuditDataVideoAssetsFilesBody>;
export type AuditVideoAssets = Record<string, AuditVideoAsset>;
export type AuditAssetsCategoryMap = Record<string, string[]>;

export type AuditData = Omit<LoadAuditDataResponseItemBody, 'categories' | 'vehicleId'> & {
  fields: AuditFields;
  assets: AuditAssets;
  videoAssets: AuditVideoAssets;
  assetsCategoryMap: AuditAssetsCategoryMap;
  isNew?: boolean;
  errorCategories: AuditCategoriesWithError;
};

const getParsedValue = (value: string): TConditionFormValue => {
  if (isJsonString(value)) {
    const parsedValue = JSON.parse(value);
    if (typeof parsedValue === 'number') {
      return value;
    }

    return parsedValue as TConditionFormValue;
  }

  return value;
};

const getFieldsAndAssetsData = (
  auditCategoriesData: LoadAuditDataCategories[]
): {
  assets: AuditAssets;
  assetsCategoryMap: AuditAssetsCategoryMap;
  videoAssets: AuditVideoAssets;
  fields: AuditFields;
} => {
  const fields: AuditFields = {};
  const assets: AuditAssets = {};
  const videoAssets: AuditVideoAssets = {};
  const assetsCategoryMap: AuditAssetsCategoryMap = {};

  auditCategoriesData.forEach((category) => {
    category.fields.forEach(({paramDefinitionId, value}) => {
      fields[getFormFieldName(category.id, paramDefinitionId)] = getParsedValue(value);
    });

    category.assets.forEach(({paramDefinitionId, files}) => {
      files.forEach((file) => {
        const name = getFormFieldName(category.id, paramDefinitionId);

        if (!assets?.[name]) {
          assets[name] = {};
        }

        if (file.imageId) {
          assets[name][file.imageId] = file;
        }
        assetsCategoryMap[category.id] = union(assetsCategoryMap[category.id] ?? [], [name]);
      });
    });

    category.videoAssets.forEach(({paramDefinitionId, videos}) => {
      videos.forEach((video) => {
        const name = getFormFieldName(category.id, paramDefinitionId);

        if (!videoAssets?.[name]) {
          videoAssets[name] = {};
        }

        if (video.videoId) {
          videoAssets[name][video.videoId] = video;
        }
        assetsCategoryMap[category.id] = union(assetsCategoryMap[category.id] ?? [], [name]);
      });
    });
  });

  return {
    fields,
    assets,
    videoAssets,
    assetsCategoryMap,
  };
};

export const getInitialAuditData = (audit?: LoadAuditDataResponseItemBody): AuditData | null => {
  if (!audit) {
    return null;
  }

  const {
    id,
    state,
    user,
    finishedAt,
    controlStructureId,
    updateAt,
    inspectionType,
    inspectionNote,
  } = audit;
  const {fields, assets, videoAssets, assetsCategoryMap} = getFieldsAndAssetsData(audit.categories);

  return {
    id,
    state,
    user,
    finishedAt,
    controlStructureId,
    updateAt,
    inspectionType,
    inspectionNote,
    fields,
    assets,
    videoAssets,
    assetsCategoryMap,
    errorCategories: {},
  };
};

export function areAuditFieldsEmpty(auditFields: AuditData['fields']) {
  const isEmptyObject = isEmpty(auditFields);

  if (isEmptyObject) {
    return true;
  }

  return !Object.values(auditFields).some((value) => value !== '');
}

export function filterFieldsToOverride(
  templateFields: AuditFields,
  actualAuditFields: AuditFields,
  categoriesKeysOfFieldsToOverride?: string[]
) {
  if (!categoriesKeysOfFieldsToOverride) {
    return {};
  }

  const mergedFields = {...templateFields, ...actualAuditFields};

  return Object.keys(mergedFields).reduce((acc, key) => {
    const {categoryId} = getCategoryIdFromFormFieldName(key);

    if (categoriesKeysOfFieldsToOverride.includes(categoryId)) {
      if (key in templateFields) {
        const templateValue = templateFields[key];

        return {
          ...acc,
          [key]: templateValue,
        };
      }

      return {
        ...acc,
        [key]: '',
      };
    }
    return acc;
  }, {} as AuditFields);
}

/**
 * Returns fields that belong to specific categories, with updated values from template.
 * For fields that are in categories but not in template, the value is cleared (set to empty string `''`).
 */
export const getAuditDataFromAutoFillTemplate = (
  actualAudit: AuditData,
  // Only the categories are read, so both an autofill template and a loaded audit can be passed
  template: Pick<LoadAuditDataResponseItemBody, 'categories'>,
  categoryIdsToOverride: string[]
) => {
  if (!template) {
    throw new Error('Template is required');
  }

  const {fields: templateFields} = getFieldsAndAssetsData(template.categories);

  const filteredTemplateFields = filterFieldsToOverride(
    templateFields,
    actualAudit.fields,
    categoryIdsToOverride
  );

  return filteredTemplateFields;
};

export function recursiveGetAllCategoriesIds(categories: AuditCategoryOfStructure[]): string[] {
  return categories.flatMap((category) => [
    category.id,
    ...recursiveGetAllCategoriesIds(category.childCategories ?? []),
  ]);
}

/**
 * Flatten the categories to one array with all ancestors and children
 */
export function flattenCategories(
  categories: AuditCategoryOfStructure[]
): AuditCategoryOfStructure[] {
  return categories.flatMap((category) => [
    category,
    ...flattenCategories(category.childCategories ?? []),
  ]);
}
